import { useApolloClient } from '@apollo/client';
import { useCallback } from 'react';

import { useIsMobileApp } from '#src/hooks/useIsMobileApp.ts';
import { useRevokeTwaDeviceToken } from '#src/hooks/useTwaPushNotifications.ts';
import { AuthBroadCastChannel } from '#src/lib/broadcastChannel.ts';
import { sendSafeError } from '#src/lib/error.ts';
import { useEvents } from '#src/lib/events/useEvents.ts';
import { client as httpClient, type HttpResponse } from '#src/lib/http.ts';
import { reloadPage } from '#src/routing/EnsureLatestAppVersion/reloadPage.ts';

export const useLogout = () => {
  const client = useApolloClient();
  const track = useEvents();
  const { isMobileApp, postMessage: mobileAppPostMessage } = useIsMobileApp();
  const revokeTwaDeviceToken = useRevokeTwaDeviceToken();

  return useCallback(async () => {
    const revokePromise = revokeTwaDeviceToken().catch(sendSafeError);

    return httpClient
      .delete('/users/sign_out.json')
      .then(async response => {
        await revokePromise;

        if (response.status === 204) {
          track('[Client] Log Out');
          if (window.analytics) {
            window.analytics.reset();
          }
        }
        await client.clearStore();
        reloadPage();

        // log out all tabs
        AuthBroadCastChannel.postMessage('logged_out');

        // log out Flutter webview-based app
        if (isMobileApp) {
          mobileAppPostMessage('logout', { isExplicit: true });
        }

        return response;
      })
      .catch((error: Error) => ({ error: error.message }));
  }, [client, isMobileApp, mobileAppPostMessage, revokeTwaDeviceToken, track]);
};

export type { HttpResponse };
